import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';
import AK0SupplyDataPage from './AK0SupplyDataPage';
import CannibalizationPage from './CannibalizationPage';
import LTDAircraftPage from './LTDAircraftPage';
import MocDailyCallDashboard from './MocDailyCallDashboard';
import StrutWaterfallPage from './StrutWaterfallPage';
import RfpaScrPage from './RfpaScrPage';
import RfpaScrForecastPage from './RfpaScrForecastPage';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        page: { backgroundColor: isDark ? '#121212' : '#f4f7f6', minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1800px', margin: '0 auto', fontFamily: 'sans-serif' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        pageTitle: { fontSize: '28px', fontWeight: 'bold', color: isDark ? '#e0e0e0' : '#333' },
        kpiGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
        },
        kpiCard: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            padding: '20px',
            borderRadius: '8px',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            boxShadow: `0 2px 5px ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
        },
        kpiValue: { fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '10px', color: isDark ? '#e0e0e0' : '#333' },
        kpiLabel: { fontSize: '1rem', color: isDark ? '#a0a0a0' : '#666' },
        kpiChange: { fontSize: '0.9rem', marginTop: '5px' },
        chartCard: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            padding: '20px',
            borderRadius: '8px',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
        },
        chartTitle: { fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: isDark ? '#e0e0e0' : '#333' },
        tabContainer: { marginBottom: '20px', borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}` },
        tab: {
            padding: '10px 20px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: 'transparent',
            color: isDark ? '#a0a0a0' : '#666',
            fontSize: '14px',
            borderBottom: `2px solid transparent`,
            marginRight: '10px'
        },
        activeTab: {
            color: isDark ? '#6cb2f7' : '#007bff',
            borderBottom: `2px solid ${isDark ? '#6cb2f7' : '#007bff'}`
        },
        link: { color: isDark ? '#6cb2f7' : '#007bff', textDecoration: 'none' }
    };
};

function ExecutiveDashboard({ theme }) {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [dashboardData, setDashboardData] = useState({
        totalAircraft: 0,
        availableAircraft: 0,
        maintenanceAircraft: 0,
        averageMaintenanceTime: 0,
        statusData: [],
        nmcsPmcsData: [],
        lastRefreshed: null,
    });
    const styles = getStyles(theme);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);

            const aircraftResponse = await api.get('/aircraft/');
            const aircraft = Array.isArray(aircraftResponse.data)
                ? aircraftResponse.data
                : (aircraftResponse.data.results || []);

            const statusResponse = await api.get('/aircraft-status/');
            const statuses = Array.isArray(statusResponse.data)
                ? statusResponse.data
                : (statusResponse.data.results || []);

            let nmcsPmcsData = [];
            try {
                const nmcsResponse = await api.get('/nmcs-pmcs/');
                nmcsPmcsData = Array.isArray(nmcsResponse.data)
                    ? nmcsResponse.data
                    : (nmcsResponse.data.results || []);
            } catch (err) {
                console.warn('Could not fetch NMCS PMCS data:', err);
            }

            const totalAircraft = aircraft.length;
            const availableAircraft = statuses.filter(s =>
                s.status === 'UP' || s.status === 'XC'
            ).length;
            const maintenanceAircraft = statuses.filter(s =>
                s.status === 'DN' || s.status === 'PM' || s.status === 'DS'
            ).length;

            const aircraftWithECD = statuses.filter(s => s.ecd);
            let totalDays = 0;
            aircraftWithECD.forEach(s => {
                if (s.edd && s.ecd) {
                    const edd = new Date(s.edd);
                    const ecd = new Date(s.ecd);
                    const days = Math.ceil((ecd - edd) / (1000 * 60 * 60 * 24));
                    if (days > 0) totalDays += days;
                }
            });
            const averageMaintenanceTime = aircraftWithECD.length > 0
                ? Math.round(totalDays / aircraftWithECD.length)
                : 0;

            setDashboardData({
                totalAircraft,
                availableAircraft,
                maintenanceAircraft,
                averageMaintenanceTime,
                statusData: statuses,
                nmcsPmcsData,
                lastRefreshed: new Date(),
            });
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const availabilityRate = dashboardData.totalAircraft > 0
        ? Math.round((dashboardData.availableAircraft / dashboardData.totalAircraft) * 100)
        : 0;

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.container}>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <Link to="/" style={styles.link}>&larr; Back to Home</Link>
                        <h1 style={styles.pageTitle}>Executive Dashboard</h1>
                    </div>
                    <HamburgerMenu theme={theme} />
                </div>

                <div style={styles.tabContainer}>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'overview' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'ak0-supply' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('ak0-supply')}
                    >
                        AK0 Supply
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'cannibalization' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('cannibalization')}
                    >
                        Cannibalization
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'ltd-aircraft' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('ltd-aircraft')}
                    >
                        LTD Aircraft
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'strut-waterfall' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('strut-waterfall')}
                    >
                        Strut Waterfall
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'rfpa-scr' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('rfpa-scr')}
                    >
                        RFPA SCR
                    </button>
                    <button
                        style={{ ...styles.tab, ...(activeTab === 'rfpa-scr-forecast' ? styles.activeTab : {}) }}
                        onClick={() => setActiveTab('rfpa-scr-forecast')}
                    >
                        RFPA/SCR Forecast
                    </button>
                </div>

                {activeTab === 'overview' && (
                    <>
                        <MocDailyCallDashboard
                            theme={theme}
                            statusData={dashboardData.statusData}
                            nmcsPmcsData={dashboardData.nmcsPmcsData}
                            lastRefreshed={dashboardData.lastRefreshed}
                        />

                        <div style={styles.kpiGrid}>
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiValue}>{dashboardData.totalAircraft}</div>
                                <div style={styles.kpiLabel}>Total Aircraft</div>
                            </div>
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiValue}>{dashboardData.availableAircraft}</div>
                                <div style={styles.kpiLabel}>Available Aircraft</div>
                                <div style={styles.kpiChange}>Availability: {availabilityRate}%</div>
                            </div>
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiValue}>{dashboardData.maintenanceAircraft}</div>
                                <div style={styles.kpiLabel}>In Maintenance</div>
                            </div>
                            <div style={styles.kpiCard}>
                                <div style={styles.kpiValue}>{dashboardData.averageMaintenanceTime}</div>
                                <div style={styles.kpiLabel}>Avg Maintenance Days</div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'ak0-supply' && (
                    <AK0SupplyDataPage theme={theme} embedded />
                )}

                {activeTab === 'cannibalization' && (
                    <CannibalizationPage theme={theme} embedded />
                )}

                {activeTab === 'ltd-aircraft' && (
                    <LTDAircraftPage theme={theme} embedded />
                )}

                {activeTab === 'strut-waterfall' && (
                    <StrutWaterfallPage theme={theme} embedded />
                )}

                {activeTab === 'rfpa-scr' && (
                    <RfpaScrPage theme={theme} embedded />
                )}

                {activeTab === 'rfpa-scr-forecast' && (
                    <RfpaScrForecastPage theme={theme} embedded />
                )}

                <div style={styles.chartCard}>
                    <div style={styles.chartTitle}>Quick Actions</div>
                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <Link to="/status-board" style={styles.link}>View Status Board</Link>
                        <Link to="/maintenance-priority" style={styles.link}>View Maintenance Priorities</Link>
                        <Link to="/reports" style={styles.link}>View Reports</Link>
                        <Link to="/aircraft-summary" style={styles.link}>Aircraft Summary</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ExecutiveDashboard;
